/**
 * Financial Dashboard KPI registry — single source of truth for the
 * owner-only /financials page.
 *
 * WHY THIS DESIGN:
 * - JSONB storage (financials_annual.annual + .monthly_targets +
 *   financials_monthly.data) instead of normalized columns: the KPI
 *   list IS the contract, and the contract lives here in TypeScript.
 *   Adding a KPI = one entry in the array below + a field on the UI.
 *   No SQL migration.
 * - Zod uses .strict() on write so misspelled keys fail loud (400).
 *   Without .strict(), JSONB is worse than normalized columns
 *   (Zod default .strip() silently drops unknown keys).
 * - DERIVED KPIs (source: 'derived') are server-owned. The value is
 *   computed at GET-time by the worker (e.g. inventory_value_cents =
 *   SUM(on_hand * cost_cents / conversion_factor) for active SKUs — cost
 *   is per purchase UOM, on_hand is in pieces, so divide by the pieces-per-
 *   purchase-unit factor; NULL/0 factor treated as 1). Derived values are
 *   FILTERED OUT of the Zod write schemas so write attempts get 400 —
 *   the value is not persisted to JSONB, only injected into the GET
 *   response. By default the _target key remains owner-entered.
 * - DERIVED TARGETS (targetSource: 'derived') extend the same idea to
 *   the _target key when its value is also computed from other targets
 *   (e.g. net_margin_pct_target = net_profit_cents_target /
 *   annual_revenue_cents_target). Use both source='derived' AND
 *   targetSource='derived' for fully-computed KPIs like Net Margin %.
 * - Per-KPI `direction` lets variance-tint normalize: "better than
 *   target" is always green, regardless of whether higher or lower
 *   is the desired direction. Owner Hours, Callback Rate, Safety
 *   Incidents are lower-better and would tint wrong without this.
 * - Two tables (annual + monthly) because they have different keys
 *   (year vs month) and different cadences. Don't smush.
 *
 * Out of scope here: CSV import, QBO sync, auto-derive from existing
 * job/inventory data. Those are future threads.
 */

import { z } from 'zod';

export type FinancialKpiUnit = 'cents' | 'pct' | 'hours' | 'int';
export type FinancialKpiDirection = 'higher_better' | 'lower_better';
export type KpiSource = 'manual' | 'derived';

export interface KpiDef {
  /** JSONB key. Stable identifier — don't rename without a backfill. */
  key: string;
  /** Display label in the table row. */
  label: string;
  /** Unit determines the formatter + input affordance + Zod constraint. */
  unit: FinancialKpiUnit;
  /** Direction normalizes variance so "better than target" is always positive. */
  direction: FinancialKpiDirection;
  /** Which dashboard table this KPI renders into (1-5; Table 6 is computed). */
  table: 1 | 2 | 3 | 4 | 5;
  /**
   * 'derived' = server-owned; value computed by worker at GET-time and
   *  filtered out of the Zod write schema (writes 400 on this key).
   * Absent or 'manual' = owner-entered through inline edit.
   */
  source?: KpiSource;
  /**
   * Same semantics as `source` but for the _target key. By default the
   * target is owner-entered ('manual') even when the value is derived.
   * Set to 'derived' for KPIs whose target is also computed from other
   * KPIs (e.g. net_margin_pct_target = net_profit_cents_target /
   * annual_revenue_cents_target).
   */
  targetSource?: KpiSource;
}

// ============================================================
// Table 1 — Annual Goals (Metric | Current | Target | Status)
// Each KPI stored as TWO JSONB keys on financials_annual.annual:
//   { <key>: <current>, <key>_target: <target> }
// ============================================================
export const ANNUAL_KPIS: KpiDef[] = [
  { key: 'annual_revenue_cents',     label: 'Annual Revenue',       unit: 'cents', direction: 'higher_better', table: 1, source: 'derived' },
  { key: 'net_profit_cents',         label: 'Net Profit (EBITDA)',  unit: 'cents', direction: 'higher_better', table: 1, source: 'derived' },
  { key: 'net_margin_pct',           label: 'Net Margin %',         unit: 'pct',   direction: 'higher_better', table: 1, source: 'derived', targetSource: 'derived' },
  { key: 'estimated_value_cents',    label: 'Estimated Value',      unit: 'cents', direction: 'higher_better', table: 1, source: 'derived', targetSource: 'derived' },
  { key: 'inventory_value_cents',    label: 'Inventory Value',      unit: 'cents', direction: 'lower_better',  table: 1, source: 'derived' },
  { key: 'owner_hours_per_week',     label: 'Owner Hours / Week',   unit: 'hours', direction: 'lower_better',  table: 1 },
  { key: 'number_of_crews',          label: 'Number of Crews',      unit: 'int',   direction: 'higher_better', table: 1 },
  { key: 'contract_revenue_pct_ann', label: 'Contract Revenue %',   unit: 'pct',   direction: 'higher_better', table: 1 },
];

// ============================================================
// Tables 2-5 — Monthly KPIs
// "This Month" stored on financials_monthly.data under <key>.
// "Target" stored on financials_annual.monthly_targets under <key>
//   (one target applies to every month of the year).
// ============================================================
export const MONTHLY_KPIS: KpiDef[] = [
  // Table 2 — Monthly Financial
  { key: 'revenue_cents',             label: 'Revenue',                    unit: 'cents', direction: 'higher_better', table: 2 },
  { key: 'gross_margin_pct',          label: 'Gross Margin %',             unit: 'pct',   direction: 'higher_better', table: 2 },
  { key: 'monthly_net_profit_cents',  label: 'Net Profit',                 unit: 'cents', direction: 'higher_better', table: 2 },
  { key: 'monthly_net_margin_pct',    label: 'Net Margin %',               unit: 'pct',   direction: 'higher_better', table: 2 },
  { key: 'revenue_per_crew_cents',    label: 'Revenue per Crew',           unit: 'cents', direction: 'higher_better', table: 2 },
  // Table 3 — Operational
  { key: 'jobs_completed',            label: 'Jobs Completed',             unit: 'int',   direction: 'higher_better', table: 3 },
  { key: 'avg_job_size_cents',        label: 'Avg Job Size',               unit: 'cents', direction: 'higher_better', table: 3 },
  { key: 'callback_rate_pct',         label: 'Callback Rate %',            unit: 'pct',   direction: 'lower_better',  table: 3 },
  { key: 'on_time_completion_pct',    label: 'On-Time Completion %',       unit: 'pct',   direction: 'higher_better', table: 3 },
  { key: 'safety_incidents',          label: 'Safety Incidents',           unit: 'int',   direction: 'lower_better',  table: 3 },
  // Table 4 — Owner Time
  { key: 'owner_hours_worked',        label: 'Owner Hours Worked',         unit: 'hours', direction: 'lower_better',  table: 4 },
  { key: 'jobs_estimated_by_owner',   label: 'Jobs Estimated by Owner',    unit: 'int',   direction: 'lower_better',  table: 4 },
  { key: 'decisions_requiring_owner', label: 'Decisions Requiring Owner',  unit: 'int',   direction: 'lower_better',  table: 4 },
  { key: 'supervisor_autonomy',       label: 'Supervisor Autonomy (1-10)', unit: 'int',   direction: 'higher_better', table: 4 },
  // Table 5 — Sales
  { key: 'contract_revenue_pct',      label: 'Contract Revenue %',         unit: 'pct',   direction: 'higher_better', table: 5 },
  { key: 'new_contracts_added',       label: 'New Contracts Added',        unit: 'int',   direction: 'higher_better', table: 5 },
  { key: 'largest_client_pct',        label: 'Largest Client %',           unit: 'pct',   direction: 'lower_better',  table: 5 },
  { key: 'close_rate_pct',            label: 'Close Rate %',               unit: 'pct',   direction: 'higher_better', table: 5 },
  { key: 'avg_job_gross_profit_cents', label: 'Avg Job Gross Profit',      unit: 'cents', direction: 'higher_better', table: 5 },
];

/**
 * Sign convention: positive ALWAYS means "better than target",
 * regardless of whether higher or lower is the desired direction.
 * Tint logic in the UI just checks signedVariance > 0 → green.
 */
export function signedVariance(
  actual: number,
  target: number,
  direction: FinancialKpiDirection,
): number {
  const raw = actual - target;
  // `+ 0` normalizes -0 → +0 so downstream comparisons and serialization
  // are stable. Without this, lower-better KPIs at-target return -0 because
  // -(actual - target) = -(0) = -0.
  return (direction === 'higher_better' ? raw : -raw) + 0;
}

// ============================================================
// Zod schemas — used by worker for request validation.
// .strict() on every shape rejects unknown keys with 400.
// Numeric values accept null (owner clears a cell).
// ============================================================

function buildShape(defs: KpiDef[], targetSuffix = false): z.ZodRawShape {
  const shape: z.ZodRawShape = {};
  for (const def of defs) {
    // Server owns derived values — reject on write.
    if (def.source !== 'derived') {
      shape[def.key] = zodForUnit(def.unit);
    }
    // Targets default to owner-entered + writable. Only skip when explicitly
    // marked as derived (e.g. net_margin_pct_target is computed from
    // net_profit_cents_target / annual_revenue_cents_target).
    if (targetSuffix && def.targetSource !== 'derived') {
      shape[`${def.key}_target`] = zodForUnit(def.unit);
    }
  }
  return shape;
}

function zodForUnit(unit: FinancialKpiUnit) {
  switch (unit) {
    case 'cents':
      // Allow negative (loss months). Integer.
      return z.number().int().nullable().optional();
    case 'int':
      return z.number().int().min(0).nullable().optional();
    case 'pct':
      // Percent can be negative (e.g. negative margin month).
      return z.number().nullable().optional();
    case 'hours':
      return z.number().min(0).nullable().optional();
  }
}

/** Body of PUT /api/financials/annual?year=YYYY */
export const financialsAnnualPutSchema = z.object({
  annual: z.object(buildShape(ANNUAL_KPIS, true)).strict(),
  monthly_targets: z.object(buildShape(MONTHLY_KPIS, false)).strict(),
}).strict();
export type FinancialsAnnualPut = z.infer<typeof financialsAnnualPutSchema>;

/** Body of PUT /api/financials/monthly?month=YYYY-MM-DD */
export const financialsMonthlyPutSchema = z.object({
  data: z.object(buildShape(MONTHLY_KPIS, false)).strict(),
}).strict();
export type FinancialsMonthlyPut = z.infer<typeof financialsMonthlyPutSchema>;

/** Response shape of GET /api/financials?year=YYYY */
export interface FinancialsResponse {
  year: number;
  annual: Record<string, number | null>;
  monthly_targets: Record<string, number | null>;
  /** Keyed by 'YYYY-MM-01' ISO date. Missing months omitted. */
  monthly_by_date: Record<string, Record<string, number | null>>;
}
